import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials, LoginResult } from '../../types/auth.types';
import TwoFactorVerification from './TwoFactorVerification';
import authService from '../../services/auth.service';
import { deviceTokenManager } from '../../utils/deviceToken';
import { tokenManager } from '../../services/api';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, completeTwoFactorLogin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState<{
    open: boolean;
    userId: number;
    method: 'email' | 'sms';
  }>({ open: false, userId: 0, method: 'email' });

  // Redirect if already authenticated
  React.useEffect(() => {
    // CRITICAL: Clear any customer view data when arriving at login page
    // This prevents data leakage between different user sessions
    sessionStorage.removeItem('loginAsCustomer');
    
    if (isAuthenticated) {
      navigate('/voicemail');
    }
  }, [isAuthenticated, navigate]);

  // Load branding - disabled, using static files instead
  useEffect(() => {
    // Static branding based on domain - no API calls needed
    // Logos and branding are loaded from manifest files
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Login] Form submitted with:', formData);
    setError('');
    setIsSubmitting(true);

    try {
      console.log('[Login] Calling login function...');
      const result = await login({
        email: formData.email,
        password: formData.password
      }) as LoginResult;
      
      console.log('[Login] Login result:', result);
      
      // Check if 2FA is required (result will have twoFactorRequired flag)
      if (result && result.twoFactorRequired) {
        console.log('[Login] 2FA required, sending code...');
        
        // Send the 2FA code
        try {
          await authService.sendTwoFactorCode(
            formData.email,
            formData.password,
            (result.method as 'email' | 'sms') || 'sms'
          );
          console.log('[Login] 2FA code sent, opening dialog');
          
          // Only open dialog if code was sent successfully
          setTwoFactorData({
            open: true,
            userId: result.userId || 0,
            method: (result.method as 'email' | 'sms') || 'sms'
          });
          setError(''); // Clear error since 2FA is expected
        } catch (sendError: any) {
          console.error('[Login] Failed to send 2FA code:', sendError);
          setError('Failed to send verification code. Please try again.');
          // Don't open the dialog if sending failed
        }
      } else {
        console.log('[Login] Login successful');
        // The login function handles storing tokens and redirecting
      }
    } catch (error: any) {
      console.error('[Login] Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTwoFactorSuccess = async (response: any) => {
    console.log('[Login] 2FA verification successful', response);
    setTwoFactorData({ open: false, userId: 0, method: 'email' });
    
    // The response from TwoFactorVerification passes response.data which contains:
    // { success: true, data: { member: {...}, access_token: '...', refresh_token: '...' } }
    // So we need to look at response.data for the actual data
    const responseData = response.data || response;
    const member = responseData.member;
    
    if (response && response.success && responseData && member && member.email) {
      // We need to properly login with the returned data
      // The 2FA endpoint returns the same structure as a successful login
      try {
        // Store tokens from the correct location in response.data
        const access_token = responseData.access_token;
        const refresh_token = responseData.refresh_token;
        
        // Store tokens properly using tokenManager (stores in localStorage, sessionStorage, and cookies)
        if (access_token) {
          tokenManager.setTokens(access_token, refresh_token);
          console.log('[Login] Tokens stored via tokenManager');
        }
        
        // Store member data - the PHP endpoint sends properly formatted data
        // Member data from PHP already has correct structure
        const memberData = {
          id: member.id,
          email: member.email,
          firstname: member.firstname,
          lastname: member.lastname,
          memberType: member.memberType,
          customerID: member.customerID,
          customerName: member.customerName || '',
          extension: member.extension,
          timezone: member.timezone || 'America/Toronto'
        };
        localStorage.setItem('member_data', JSON.stringify(memberData));
        console.log('[Login] Member data stored:', memberData);
        
        // Store device token if provided (when "Remember this device" was checked)
        if (responseData.device_token) {
          deviceTokenManager.setToken(responseData.device_token);
          console.log('[Login] Device trusted for 30 days (stored in cookie and localStorage)');
        }
        
        // Use the AuthContext to properly complete login
        // This sets the member state and navigates to voicemail
        console.log('[Login] Completing 2FA login via AuthContext...');
        completeTwoFactorLogin(memberData);
      } catch (error) {
        console.error('[Login] Error handling 2FA success:', error);
        setError('Failed to complete login after 2FA verification');
      }
    } else {
      console.error('[Login] No member data in 2FA response:', response);
      // Log the full response structure to debug
      console.log('[Login] Full 2FA response structure:', JSON.stringify(response, null, 2));
      setError('Invalid response from 2FA verification');
    }
  };

  const handleTwoFactorCancel = () => {
    setTwoFactorData({ open: false, userId: 0, method: 'email' });
    setFormData({ ...formData, password: '' });
    setError('Two-factor authentication cancelled');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper
          elevation={isMobile ? 0 : 3}
          sx={{
            padding: isMobile ? 2 : 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          
          <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
            Portal Login
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              autoComplete="username"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              inputProps={{
                autoComplete: 'username',
                'data-form-type': 'email',
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  color="primary"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              }
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Forgot password?
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      <TwoFactorVerification
        open={twoFactorData.open}
        userId={twoFactorData.userId}
        method={twoFactorData.method}
        onSuccess={handleTwoFactorSuccess}
        onCancel={handleTwoFactorCancel}
      />
    </Container>
  );
};

export default Login;